import React, { useState } from "react";
import { fmtDateTime } from "../lib/dates";
import { Smartphone, ShieldCheck, Trash2 } from "lucide-react";
import { toast } from "sonner";
import api, { formatError } from "../lib/api";
import { useApi } from "../hooks/useApi";
import { PasskeyPanel, SecurityNotices } from "./SecurityExtras";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";

export const SecurityPanel = () => {
  const { data: devices, reload } = useApi("/auth/devices");
  const [pw, setPw] = useState({ current_password: "", new_password: "" });
  const [busy, setBusy] = useState(false);

  const change = async () => {
    setBusy(true);
    try {
      await api.post("/auth/change-password", pw);
      toast.success("Password changed. Other sessions have been signed out.");
      setPw({ current_password: "", new_password: "" });
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); } finally { setBusy(false); }
  };
  const forget = async (id) => { await api.delete(`/auth/devices/${id}`); reload(); toast.success("Device forgotten"); };

  return (
    <div className="glass rounded-2xl p-6 space-y-5" data-testid="security-panel">
      <h3 className="flex items-center gap-2 font-serif-display text-xl font-bold text-crimson"><ShieldCheck size={20} /> Security</h3>
      <div className="space-y-2">
        <PasskeyPanel />
        <p className="mt-4 text-sm font-medium">Known devices</p>
        <p className="text-xs text-muted-foreground">We alert you whenever your account signs in from a device not on this list.</p>
        {(devices || []).map((d) => (
          <div key={d.id} className="flex items-center justify-between rounded-lg border border-gold/20 bg-parchment-card p-3 text-sm" data-testid={`device-${d.id}`}>
            <div className="flex items-center gap-2">
              <Smartphone size={16} className="text-palm" />
              <div>
                <p className="font-medium">{d.label} {d.current && <span className="ml-1 rounded bg-palm/10 px-1.5 py-0.5 text-[10px] text-palm">This device</span>}</p>
                <p className="text-xs text-muted-foreground">Last seen {fmtDateTime(d.last_seen)} · IP {d.ip}</p>
              </div>
            </div>
            {!d.current && <Button size="sm" variant="ghost" onClick={() => forget(d.id)} className="text-destructive" data-testid={`device-forget-${d.id}`}><Trash2 size={14} /></Button>}
          </div>
        ))}
      </div>
      <div className="space-y-2 border-t border-gold/20 pt-4">
        <p className="text-sm font-medium">Change password</p>
        <div><Label>Current password</Label><Input type="password" value={pw.current_password} onChange={(e) => setPw({ ...pw, current_password: e.target.value })} data-testid="pw-current" /></div>
        <div><Label>New password (min 8 characters)</Label><Input type="password" value={pw.new_password} onChange={(e) => setPw({ ...pw, new_password: e.target.value })} data-testid="pw-new" /></div>
        <Button onClick={change} disabled={busy || pw.new_password.length < 8 || !pw.current_password} className="w-full bg-crimson hover:bg-crimson-hover" data-testid="pw-change-btn">Update password</Button>
      </div>
    </div>
  );
};
